import { motion } from 'framer-motion';
import {
  ArrowRight,
  Code2,
  Key,
  QrCode,
  Server,
  Shield,
  User,
  Wallet,
  Zap,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import SEOHead from '../components/SEOHead';

const PlatformOverview = () => {
  const features = [
    {
      icon: Shield,
      title: 'Verifiable Credentials',
      description:
        'Issue and verify W3C Verifiable Credentials with OpenID4VC flows that work across wallets.',
    },
    {
      icon: Key,
      title: 'Decentralized Identifiers (DIDs)',
      description:
        'Create and resolve DIDs. Support multiple DID methods and managed keys with cloud KMS.',
    },
    {
      icon: Wallet,
      title: 'Wallet SDK',
      description:
        'Embed a non-custodial wallet in minutes. Cross-platform SDKs for web and mobile.',
    },
    {
      icon: QrCode,
      title: 'Verifier Toolkit',
      description:
        'Low-latency verification APIs, QR flows, and policies to gate access and prevent fraud.',
    },
    {
      icon: Code2,
      title: 'Developer-first',
      description:
        'Modern REST/JSON APIs, OpenAPI spec, examples, and quickstarts to ship fast.',
    },
    {
      icon: Server,
      title: 'Deploy Anywhere',
      description:
        'Cloud, on-prem, or hybrid. Use managed services or run components in your VPC.',
    },
  ];

  const platformStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: 'Documint Platform Overview',
    description:
      'Complete identity infrastructure built on W3C standards with enterprise-grade security for issuing, verifying, and managing digital identities.',
    url: 'https://documint.id/platform-overview',
    applicationCategory: 'SecurityApplication',
    featureList: features.map(feature => feature.title),
    provider: {
      '@type': 'Organization',
      name: 'Documint',
    },
  };

  return (
    <div className="min-h-screen pt-20">
      <SEOHead
        title="Platform Overview - Complete Identity Infrastructure"
        description="Documint Platform provides complete identity infrastructure built on W3C standards. Issue, verify & manage digital identities with cryptographic proof and selective disclosure."
        keywords="identity infrastructure, W3C standards, digital identity platform, verifiable credentials platform, DID platform, OpenID4VC, enterprise identity, cryptographic security, identity management"
        url="https://documint.id/platform-overview"
        structuredData={platformStructuredData}
      />
      {/* Hero Section */}
      <section className="relative overflow-hidden py-20 bg-surface-100">
        <div className="container-custom text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-4xl lg:text-6xl font-bold mb-6 relative">
              <span className="bg-gradient-to-r from-white via-gray-100 to-white bg-clip-text text-transparent drop-shadow-lg">
                Platform{' '}
              </span>
              <span className="bg-gradient-to-r from-neon-300 via-neon-400 to-primary-400 bg-clip-text text-transparent animate-pulse drop-shadow-2xl">
                Overview
              </span>
              <div className="absolute -inset-2 bg-gradient-to-r from-neon-600/10 via-primary-600/10 to-neon-600/10 blur-xl -z-10 rounded-lg"></div>
            </h1>
            <p className="text-xl text-gray-300 max-w-4xl mx-auto mb-8">
              <strong className="text-white">
                Documint Platform provides the complete identity infrastructure
              </strong>{' '}
              for modern applications. Built on W3C standards with
              enterprise-grade security, our platform enables you to issue,
              verify, and manage digital identities with cryptographic proof and
              selective disclosure capabilities.
            </p>
            <Link
              to="/pricing"
              className="btn-primary inline-flex items-center"
            >
              Start Building
              <ArrowRight size={20} className="ml-2" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Technical Architecture Section */}
      <section className="py-20 bg-surface-200/40">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">
              Technical Architecture
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Built on industry standards with enterprise-grade security and
              scalability
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="glass p-6 rounded-xl"
            >
              <div className="w-12 h-12 bg-primary-600/20 rounded-lg flex items-center justify-center mb-4">
                <Server className="w-6 h-6 text-primary-400" />
              </div>
              <h3 className="text-xl font-bold text-white mb-3">
                W3C Standards
              </h3>
              <p className="text-gray-300 text-sm leading-relaxed">
                Built on W3C DID and Verifiable Credentials standards ensuring
                interoperability across all platforms and wallets.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="glass p-6 rounded-xl"
            >
              <div className="w-12 h-12 bg-neon-600/20 rounded-lg flex items-center justify-center mb-4">
                <Shield className="w-6 h-6 text-neon-400" />
              </div>
              <h3 className="text-xl font-bold text-white mb-3">
                Cryptographic Security
              </h3>
              <p className="text-gray-300 text-sm leading-relaxed">
                Advanced cryptographic signatures using Ed25519 and secp256k1
                curves for maximum security and performance.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="glass p-6 rounded-xl"
            >
              <div className="w-12 h-12 bg-green-600/20 rounded-lg flex items-center justify-center mb-4">
                <QrCode className="w-6 h-6 text-green-400" />
              </div>
              <h3 className="text-xl font-bold text-white mb-3">OpenID4VC</h3>
              <p className="text-gray-300 text-sm leading-relaxed">
                OpenID for Verifiable Credentials protocol enabling seamless
                issuance and verification flows.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
              className="glass p-6 rounded-xl"
            >
              <div className="w-12 h-12 bg-blue-600/20 rounded-lg flex items-center justify-center mb-4">
                <Key className="w-6 h-6 text-blue-400" />
              </div>
              <h3 className="text-xl font-bold text-white mb-3">
                Multi-Chain Support
              </h3>
              <p className="text-gray-300 text-sm leading-relaxed">
                Support for multiple blockchain networks including Ethereum,
                Algorand, and Hyperledger for DID anchoring.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.5 }}
              className="glass p-6 rounded-xl"
            >
              <div className="w-12 h-12 bg-purple-600/20 rounded-lg flex items-center justify-center mb-4">
                <Wallet className="w-6 h-6 text-purple-400" />
              </div>
              <h3 className="text-xl font-bold text-white mb-3">
                Universal Wallet
              </h3>
              <p className="text-gray-300 text-sm leading-relaxed">
                Cross-platform wallet SDKs for web, mobile, and desktop
                applications with biometric security.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.6 }}
              className="glass p-6 rounded-xl"
            >
              <div className="w-12 h-12 bg-orange-600/20 rounded-lg flex items-center justify-center mb-4">
                <Zap className="w-6 h-6 text-orange-400" />
              </div>
              <h3 className="text-xl font-bold text-white mb-3">
                High Performance
              </h3>
              <p className="text-gray-300 text-sm leading-relaxed">
                Optimized for scale with sub-second verification times and
                enterprise-grade infrastructure.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Build Wallets, Issuers and Verifiers Section */}
      <section className="py-20 bg-surface-100">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">
              Build Wallets, Issuers and Verifiers
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Standards-first building blocks, high‑level SDKs and hosted
              services so you can launch privacy‑preserving identity experiences
              in days.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="rounded-xl p-6 card-hover"
              >
                <div className="w-12 h-12 bg-primary-600/20 rounded-lg flex items-center justify-center mb-4">
                  <feature.icon className="w-6 h-6 text-neon-300" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-3">
                  {feature.title}
                </h3>
                <p className="text-gray-300">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-20 bg-gradient-to-r from-primary-600 to-neon-600">
        <div className="container-custom text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">
              Ready to Build with Documint?
            </h2>
            <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
              Join hundreds of organizations already using our platform to power
              their identity infrastructure.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="https://app.passhub.documint.id/signup"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-white text-primary-600 hover:bg-gray-100 px-8 py-3 rounded-lg font-semibold transition-colors duration-200 inline-flex items-center"
              >
                Start Free Trial
                <ArrowRight size={20} className="ml-2" />
              </a>
              <Link
                to="/docs"
                className="border border-white/20 text-white hover:bg-white/10 px-8 py-3 rounded-lg font-semibold transition-colors duration-200 inline-flex items-center"
              >
                View Documentation
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default PlatformOverview;
